import React, {
    useState, useEffect, Dispatch, SetStateAction,
} from 'react';
import { ProFormaGenericRow } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import ProFormaGenericTable from 'views/ProFormaTable/components/ProFormaGenericTable/ProFormaGenericTable';
import { ProFormaModelIds } from 'constants/proForma';
import { PROCEEDS_COLUMNS } from './constants';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import UnitTypeHeader from '../UnitTypeHeaderDropdown/UnitTypeHeader';
import { createRow, getSaleProceedsData, getUnitTypeOptions } from '../helpers';
import { APARTMENT_UNIT_TYPE_NAME, NON_APARTMENT_UNIT_TYPE_NAME, dataKeyByUnitType } from '../constants';

export interface IProFormaSaleProceedsProps {
    selectedType: string;
    setSelectedType: Dispatch<SetStateAction<string>>;
}

const ProFormaSaleProceeds = ({ selectedType, setSelectedType }: IProFormaSaleProceedsProps) => {
    const { disposition, proForma, operatingInfo } = useProFormaSocketContext();

    const [proceedsRows, setProceedsRows] = useState<ProFormaGenericRow[]>([]);
    const [netSaleRows, setNetSaleRows] = useState<ProFormaGenericRow[]>([]);

    const unitTypeOptions = getUnitTypeOptions(operatingInfo, proForma);
    const dataKey = dataKeyByUnitType[selectedType];

    const proceedsColumns = PROCEEDS_COLUMNS.map(column => {
        if (column.key === 'valueByType') {
            return {
                ...column,
                header: <UnitTypeHeader
                    options={unitTypeOptions}
                    selectedType={selectedType}
                    onTypeChange={setSelectedType as any}
                />,
            };
        }
        return column;
    });

    const netSaleProceedsData = getSaleProceedsData(disposition, 'netSaleProceeds');
    const netSaleProceedsTotal = {
        valueByType: String(netSaleProceedsData?.[dataKey] ?? 0),
        amount: String(netSaleProceedsData?.amount ?? 0),
    };

    const equityProceedsData = getSaleProceedsData(disposition, 'equityProceedsFromSale');
    const equityProceedsTotal = {
        valueByType: String(equityProceedsData?.[dataKey] ?? 0),
        amount: String(equityProceedsData?.amount ?? 0),
    };

    useEffect(() => {
        const newType = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma?.proFormaModelId)
            ? APARTMENT_UNIT_TYPE_NAME.UNIT : NON_APARTMENT_UNIT_TYPE_NAME.GSF;
        setSelectedType(newType);
    }, [proForma?.proFormaModelId]);

    useEffect(() => {
        const proceeds: ProFormaGenericRow[] = [];
        const netSale: ProFormaGenericRow[] = [];

        const grossSaleData = getSaleProceedsData(disposition, 'grossSaleProceeds');
        const grossSaleRow = createRow('grossSale', 'Gross sale proceeds', grossSaleData, selectedType);
        proceeds.push(grossSaleRow);

        const sellingCostsData = getSaleProceedsData(disposition, 'sellingCosts');
        const sellingCostsRow = createRow('sellingCost', 'Selling costs', {
            perUnit: sellingCostsData?.perUnit ? -(sellingCostsData.perUnit) : 0,
            perSf: sellingCostsData?.perSf ? -(sellingCostsData.perSf) : 0,
            perGrossSf: sellingCostsData?.perGrossSf ? -(sellingCostsData.perGrossSf) : 0,
            perKw: sellingCostsData?.perKw ? -(sellingCostsData.perKw) : 0,
            perMw: sellingCostsData?.perMw ? -(sellingCostsData.perMw) : 0,
            perRack: sellingCostsData?.perRack ? -(sellingCostsData.perRack) : 0,
            amount: sellingCostsData?.amount ? -(sellingCostsData.amount) : 0,
        }, selectedType);
        proceeds.push(sellingCostsRow);

        setProceedsRows(proceeds);

        const loanPayoffData = getSaleProceedsData(disposition, 'loanPayoff');
        const loanPayoffRow = createRow('loanPayoff', 'Loan payoff', {
            perUnit: loanPayoffData?.perUnit || 0,
            perSf: loanPayoffData?.perSf || 0,
            perGrossSf: loanPayoffData?.perGrossSf || 0,
            perKw: loanPayoffData?.perKw || 0,
            perMw: loanPayoffData?.perMw || 0,
            perRack: loanPayoffData?.perRack || 0,
            amount: loanPayoffData?.amount || 0,
        }, selectedType);
        netSale.push(loanPayoffRow);

        setNetSaleRows(netSale);
    }, [disposition, createRow, getSaleProceedsData, selectedType]);

    return (
        <div className="d-flex flex-column p-3">
            <ProFormaGenericTable
                columns={proceedsColumns}
                rows={proceedsRows}
                setRows={setProceedsRows}
                hideAddButton
                totalText="Net sale proceeds"
                totalValues={netSaleProceedsTotal}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
            <ProFormaGenericTable
                columns={proceedsColumns}
                rows={netSaleRows}
                setRows={setNetSaleRows}
                hideHeader
                hideAddButton
                totalText="Equity proceeds from sale"
                totalValues={equityProceedsTotal}
                hideTotalFooter={false}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
        </div>
    );
};

export default ProFormaSaleProceeds;
